/**
 * Continuous-Conversation-Session.
 *
 * Loop: listen → STT → chat (SSE) → TTS → (auto re-listen oder idle)
 *
 * Zwei Modi:
 *  - tap (default): Selman tappt 1× auf den Orb → Session beginnt → Auto-VAD
 *    erkennt Sprechende/Stille → sendet Audio.
 *  - ptt: Push-to-Talk — Selman hält den Orb gedrückt → Aufnahme läuft → loslassen
 *    sendet Audio. Robuster auf iOS Safari.
 */

import { getToken, streamChat, synthesize, transcribeAudio } from "./api";
import { Orb } from "./orb";
import { RealtimeClient, type RealtimeState } from "./realtime-client";
import { Transcript, type BubbleHandle, type ToolBadgeHandle } from "./transcript";
import { attachAudioAnalyser, ContinuousMic } from "./vad";

export interface SessionConfig {
  voice: string;
  autoListen: boolean;
  pushToTalk: boolean;
  /** "realtime" (OpenAI Realtime API, default) oder "classic" (Whisper-Pipeline). */
  mode: "realtime" | "classic";
  /**
   * Lautsprecher-Modus: Half-Duplex erzwingen + härteres VAD-Tuning. Default
   * `false` (AirPods/Headset). Wenn Selman übers iPhone-Lautsprecher redet,
   * unterdrückt der Modus den Akustik-Loop (JARVIS hört sich selbst).
   */
  speakerMode: boolean;
}

export class VoiceSession {
  private orb: Orb;
  private transcript: Transcript;
  private mic: ContinuousMic | null = null;
  private currentAudio: HTMLAudioElement | null = null;
  private audioAnalyser: { read(): number; dispose(): void } | null = null;
  private speakingRafId = 0;
  private chatAbort: AbortController | null = null;
  private active = false;
  private busy = false;
  private muted = false;
  private cfg: SessionConfig;
  private onConfigChange?: (cfg: SessionConfig) => void;
  private onActiveChange?: (active: boolean) => void;

  // Realtime-Mode-State
  private realtime: RealtimeClient | null = null;
  private realtimeUserBubble: BubbleHandle | null = null;
  /** Aggregierter Text der laufenden User-Bubble (kann mehrere STT-Items enthalten). */
  private realtimeUserDraftText = "";
  /** Safety-Timeout-ID für "..." Placeholder wenn Whisper kein Transcript liefert. */
  private userBubbleTimeoutId = 0;
  /** Kurzer Debounce: mehrere completed-Events zu EINER finalen User-Bubble mergen. */
  private realtimeUserFinalizeTimerId = 0;
  private realtimeAssistantBubble: BubbleHandle | null = null;
  /**
   * Aktive Tool-Badges, gemappt nach Tool-Name. Wird beim toolStarted erzeugt
   * und beim toolDone geschlossen (✓/✕). Map statt single, weil OpenAI mehrere
   * Tools parallel callen kann (selten, aber möglich).
   */
  private activeToolBadges = new Map<string, ToolBadgeHandle>();

  constructor(opts: {
    orb: Orb;
    transcript: Transcript;
    config: SessionConfig;
    onActiveChange?: (active: boolean) => void;
    onConfigChange?: (cfg: SessionConfig) => void;
  }) {
    this.orb = opts.orb;
    this.transcript = opts.transcript;
    this.cfg = opts.config;
    this.onActiveChange = opts.onActiveChange;
    this.onConfigChange = opts.onConfigChange;
  }

  isActive(): boolean {
    return this.active;
  }

  getConfig(): SessionConfig {
    return { ...this.cfg };
  }

  updateConfig(patch: Partial<SessionConfig>): void {
    const prev = this.cfg;
    this.cfg = { ...this.cfg, ...patch };
    this.onConfigChange?.(this.getConfig());
    // Mode-Wechsel im laufenden Betrieb → aktuelle Session beenden, Selman muss
    // neu starten. (Wir wollen keine implizite Mid-Stream-Migration.)
    if (prev.mode !== this.cfg.mode && this.active) {
      this.stop();
    }
    // Speaker-Mode kann live umgeschaltet werden — Client-Gate folgt sofort.
    // Die VAD-Settings ändern sich erst beim nächsten Connect (session.update
    // ist statisch nach Open). Selman muss für volles Tuning Stop+Start machen.
    if (prev.speakerMode !== this.cfg.speakerMode) {
      this.realtime?.setSpeakerMode(this.cfg.speakerMode);
      this.orb.setSpeakerMode(this.cfg.speakerMode);
    }
  }

  /** Tap auf Orb → State-Maschine entscheidet (nur Tap-Modus). */
  async toggle(): Promise<void> {
    if (this.cfg.pushToTalk) return; // PTT nutzt beginPush/endPush statt toggle
    if (this.cfg.mode === "realtime") {
      await this.toggleRealtime();
      return;
    }
    const state = this.orb.getState();
    if (!this.active) {
      this.active = true;
      this.onActiveChange?.(true);
      await this.startListening();
      return;
    }
    if (state === "listening") {
      // Frühes Beenden — direkt senden was wir haben.
      this.mic?.finish();
      return;
    }
    if (state === "thinking") {
      this.cancelChat();
      this.stop();
      return;
    }
    if (state === "speaking") {
      // Barge-In: TTS unterbrechen, wieder ins Listening
      this.stopAudio();
      await this.startListening();
      return;
    }
    // Fallback
    await this.startListening();
  }

  /** Tap im Realtime-Mode: Toggle Verbindung an/aus. */
  private async toggleRealtime(): Promise<void> {
    if (this.realtime) {
      // Schon verbunden → beenden.
      this.stop();
      return;
    }
    const token = getToken();
    if (!token) {
      this.fail(new Error("Nicht eingeloggt"));
      return;
    }
    this.active = true;
    this.onActiveChange?.(true);
    this.orb.setStatus("Verbinde mit JARVIS …");
    this.orb.setState("thinking");

    // Orb über Speaker-Mode informieren, damit das speaking-Label korrekt rendert.
    this.orb.setSpeakerMode(this.cfg.speakerMode);
    const client = new RealtimeClient({
      token,
      voice: this.cfg.voice,
      speakerMode: this.cfg.speakerMode,
      handlers: {
        onState: (s) => this.applyRealtimeState(s),
        onUserSpeechStarted: () => {
          // Sofort Placeholder-Bubble für User anlegen, BEVOR JARVIS antwortet.
          // Whisper-Transkription kommt erst sekunden später — ohne Placeholder
          // erscheint die User-Bubble nach JARVIS' Antwort (falsche Reihenfolge).
          if (this.realtimeUserFinalizeTimerId) {
            clearTimeout(this.realtimeUserFinalizeTimerId);
            this.realtimeUserFinalizeTimerId = 0;
          }
          if (!this.realtimeUserBubble && this.realtimeUserDraftText.trim()) {
            this.transcript.appendTurn("you", this.realtimeUserDraftText);
            this.realtimeUserDraftText = "";
          }
          if (!this.realtimeUserBubble) {
            this.realtimeUserBubble = this.transcript.beginBubble("you", "…");
          }
          // Safety-Net: wenn Whisper noch kein transcription.completed schickt,
          // ist der Turn ohnehin "verschluckt" — Placeholder wegräumen damit
          // kein "..." dauerhaft im UI hängt.
          //
          // 6s war zu kurz: bei einer langen continuous Speech (60s+) braucht
          // Whisper-1 selbst bei 24 kHz PCM 5–10s zum Transkribieren — und
          // Server-VAD feuert speech_started einmal am Start, nicht nochmal
          // währenddessen. Der Placeholder verschwand also mitten in der
          // Speech, und wenn die transcription.completed später ankam, fiel sie
          // in den Fallback-Pfad (neue Bubble, nicht der Placeholder) — Tail-
          // Text erschien NICHT am erwarteten Ort und sah verschluckt aus.
          // 90s deckt Worst-Case (mehrere Minuten Audio + Whisper-Latenz).
          if (this.userBubbleTimeoutId) clearTimeout(this.userBubbleTimeoutId);
          this.userBubbleTimeoutId = window.setTimeout(() => {
            if (this.realtimeUserBubble) {
              this.realtimeUserBubble.discard();
              this.realtimeUserBubble = null;
              this.realtimeUserDraftText = "";
            }
          }, 90_000);
        },
        onUserTranscript: (text, final) => {
          // Safety-Timeout abschalten — Transcript ist da.
          if (this.userBubbleTimeoutId) {
            clearTimeout(this.userBubbleTimeoutId);
            this.userBubbleTimeoutId = 0;
          }
          const cleaned = text.trim();
          if (!cleaned) {
            if (final) this.scheduleRealtimeUserFinalize(1200);
            return;
          }
          if (!this.realtimeUserBubble) {
            this.realtimeUserBubble = this.transcript.beginBubble("you");
          }
          this.realtimeUserDraftText = mergeTranscriptText(this.realtimeUserDraftText, cleaned);
          this.realtimeUserBubble.setText(this.realtimeUserDraftText);

          // Bug-Analyse:
          // Bei langen 30s+ Sprachturns kann die Realtime-STT den Text in
          // mehrere completed-Events splitten. Vorher wurde die User-Bubble beim
          // ersten completed sofort finalisiert; spätere Segmente liefen dann
          // in Fallback-Pfade oder gingen verloren. Jetzt bleibt die Bubble als
          // Draft offen, merged alle Segmente, und finalisiert erst kurz nach
          // Ruhe bzw. beim Assistant-Start.
          if (final) {
            this.scheduleRealtimeUserFinalize(1200);
          }
        },
        onAssistantTranscript: (text, final) => {
          // User-Turn vor Assistant-Turn hart finalisieren, damit Reihenfolge
          // stabil bleibt und kein User-Tail mehr offen hängen bleibt.
          this.finalizeRealtimeUserBubble();
          if (!this.realtimeAssistantBubble && text) {
            this.realtimeAssistantBubble = this.transcript.beginBubble("jarvis");
          }
          if (!this.realtimeAssistantBubble) return;
          // text ist immer das aggregierte Vollbild, wir setzen den Body direkt.
          this.realtimeAssistantBubble.setText(text);
          if (final) {
            this.realtimeAssistantBubble.finalize(text);
            this.realtimeAssistantBubble = null;
          }
        },
        onAssistantAudioLevel: (rms) => {
          if (this.orb.getState() === "speaking") this.orb.setLevel(rms);
        },
        onMicLevel: (rms) => {
          if (this.orb.getState() === "listening") this.orb.setLevel(rms);
        },
        onToolStarted: (name) => {
          this.orb.setStatus(`Tool: ${name} …`);
          // Falls schon eine offene Badge mit gleichem Namen existiert (selten,
          // Edge-Case bei schnellem Re-Call), erst alte schließen.
          this.activeToolBadges.get(name)?.setStatus(true);
          const badge = this.transcript.appendToolBadge(name);
          this.activeToolBadges.set(name, badge);
        },
        onToolDone: (name, ok, summary) => {
          this.orb.setStatus(ok ? null : `Tool ${name} fail`);
          const badge = this.activeToolBadges.get(name);
          if (badge) {
            badge.setStatus(ok);
            this.activeToolBadges.delete(name);
          }
          // DALL-E / Image-Generation: Bild direkt unter der laufenden Assistant-
          // Bubble (oder einer neuen JARVIS-Bubble) rendern. Tool-Summary enthält
          // "URL: <https://…>" und/oder "Datei: <path>" — wir picken die URL.
          if (ok && /image|dalle/i.test(name)) {
            const imageUrl = extractImageUrl(summary);
            if (imageUrl) {
              // Wenn JARVIS aktuell schon eine Antwort streamt, hängen wir das Bild
              // an die laufende Bubble. Sonst: neue jarvis-Bubble nur für das Bild.
              if (this.realtimeAssistantBubble) {
                this.realtimeAssistantBubble.setImage(imageUrl);
              } else {
                const imgBubble = this.transcript.beginBubble("jarvis");
                imgBubble.setImage(imageUrl);
                imgBubble.finalize();
              }
            }
          }
        },
        onHallucinationFiltered: (original) => {
          // Nur Konsolen-Hinweis — wir wollen Selman nicht stören.
          console.info("[jarvis:realtime] hallucination filtered:", original);
        },
        onError: (msg) => {
          this.transcript.appendTurn("error", msg);
          this.orb.setStatus(msg);
          this.orb.setState("error");
        },
      },
    });
    this.realtime = client;
    try {
      await client.start();
    } catch (err) {
      this.fail(err);
    }
  }

  private applyRealtimeState(s: RealtimeState): void {
    switch (s) {
      case "connecting":
        this.orb.setState("thinking");
        this.orb.setStatus("Verbinde …");
        break;
      case "ready":
      case "listening":
        this.orb.setStatus(null);
        this.orb.setState("listening");
        break;
      case "thinking":
        this.orb.setStatus(null);
        this.orb.setState("thinking");
        break;
      case "speaking":
        this.orb.setStatus(null);
        this.finalizeRealtimeUserBubble();
        this.orb.setState("speaking");
        break;
      case "closed":
        this.orb.setStatus(null);
        this.orb.setState("idle");
        this.realtime = null;
        this.discardRealtimeUserBubble();
        this.realtimeAssistantBubble = null;
        this.active = false;
        this.onActiveChange?.(false);
        break;
      case "error":
        this.orb.setState("error");
        this.realtime = null;
        this.discardRealtimeUserBubble();
        this.realtimeAssistantBubble = null;
        this.active = false;
        this.onActiveChange?.(false);
        break;
    }
  }

  private scheduleRealtimeUserFinalize(delayMs: number): void {
    if (this.realtimeUserFinalizeTimerId) clearTimeout(this.realtimeUserFinalizeTimerId);
    this.realtimeUserFinalizeTimerId = window.setTimeout(() => {
      this.realtimeUserFinalizeTimerId = 0;
      this.finalizeRealtimeUserBubble();
    }, delayMs);
  }

  private finalizeRealtimeUserBubble(): void {
    if (this.realtimeUserFinalizeTimerId) {
      clearTimeout(this.realtimeUserFinalizeTimerId);
      this.realtimeUserFinalizeTimerId = 0;
    }
    if (this.userBubbleTimeoutId) {
      clearTimeout(this.userBubbleTimeoutId);
      this.userBubbleTimeoutId = 0;
    }
    if (!this.realtimeUserBubble) return;
    const text = this.realtimeUserDraftText.trim();
    if (text) this.realtimeUserBubble.finalize(text);
    else this.realtimeUserBubble.discard();
    this.realtimeUserBubble = null;
    this.realtimeUserDraftText = "";
  }

  private discardRealtimeUserBubble(): void {
    if (this.realtimeUserFinalizeTimerId) {
      clearTimeout(this.realtimeUserFinalizeTimerId);
      this.realtimeUserFinalizeTimerId = 0;
    }
    if (this.userBubbleTimeoutId) {
      clearTimeout(this.userBubbleTimeoutId);
      this.userBubbleTimeoutId = 0;
    }
    if (this.realtimeUserBubble) this.realtimeUserBubble.discard();
    this.realtimeUserBubble = null;
    this.realtimeUserDraftText = "";
  }

  /**
   * Selman schickt einen Text (statt zu sprechen). Funktioniert in beiden
   * Modi:
   *   - Realtime: wenn Session läuft → direkt durch RealtimeClient.sendUserText.
   *     Wenn Session noch nicht offen → erst Session starten, dann Text senden.
   *   - Classic: User-Bubble persistieren + /chat → TTS-Flow.
   *
   * @param opts.textOnlyReply Wenn true → JARVIS antwortet nur als Text
   *   (kein Audio-Stream). Nur Realtime-Mode unterstützt das per-response;
   *   Classic-Mode skipt die TTS-Synthese am Ende.
   */
  async sendText(text: string, opts?: { textOnlyReply?: boolean }): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) return;
    const textOnly = !!opts?.textOnlyReply;

    if (this.cfg.mode === "realtime") {
      // Session sicherstellen (Start ist eine User-Gesture-Kette → kommt vom Button-Submit).
      if (!this.realtime) {
        await this.toggleRealtime();
        // Auf "ready" warten (max 5s) bevor wir den Text reinpusten.
        await this.waitForRealtimeReady(5000);
      }
      // User-Bubble sofort anlegen (kommt nicht via Realtime-Transkript zurück, weil kein STT).
      this.transcript.appendTurn("you", trimmed);
      this.realtime?.sendUserText(trimmed, textOnly);
      return;
    }

    // Classic-Mode: kein Mic — direkt User-Bubble + Chat-Turn + (TTS).
    if (this.busy) return;
    this.stopAudio();
    if (this.mic) {
      this.mic.cancel();
      this.mic = null;
    }
    this.active = true;
    this.busy = true;
    this.onActiveChange?.(true);
    this.transcript.appendTurn("you", trimmed);
    await this.runChatTurn(trimmed, { skipSpeak: textOnly });
  }

  /**
   * Bild + Prompt in laufende Realtime-Session schicken.
   * Classic-Mode unterstützt kein Bild-Context-Injection.
   */
  async sendImage(imageDataUrl: string, prompt: string, fileName?: string): Promise<void> {
    const image = imageDataUrl.trim();
    const text = prompt.trim() || "Was siehst du auf diesem Bild?";
    if (!image) return;
    if (this.cfg.mode !== "realtime") {
      throw new Error("Bild-Upload ist nur im Realtime-Mode verfügbar.");
    }
    if (!this.realtime) {
      await this.toggleRealtime();
      await this.waitForRealtimeReady(5000);
    }
    const label = fileName?.trim() ? `Bild hochgeladen (${fileName.trim()})` : "Bild hochgeladen";
    this.transcript.appendTurn("you", `${label}\n${text}`);
    this.realtime?.sendUserImage(image, text);
  }

  private waitForRealtimeReady(timeoutMs: number): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!this.realtime) return resolve();
      const startedAt = Date.now();
      const tick = () => {
        const s = this.realtime?.getState();
        if (!this.realtime || s === "ready" || s === "listening" || s === "speaking") {
          return resolve();
        }
        if (s === "error" || s === "closed") return resolve();
        if (Date.now() - startedAt > timeoutMs) return resolve();
        setTimeout(tick, 80);
      };
      tick();
    });
  }

  /** Push-to-Talk: Selman drückt den Orb. Mic SOFORT öffnen (User-Gesture!). */
  async beginPushToTalk(): Promise<void> {
    if (!this.cfg.pushToTalk) return;
    if (this.cfg.mode === "realtime") return; // Server-VAD übernimmt im Realtime-Mode
    if (this.busy) return;
    if (this.mic) return; // schon offen
    this.stopAudio();
    if (!this.active) {
      this.active = true;
      this.onActiveChange?.(true);
    }
    this.orb.setStatus(null);
    this.orb.setState("listening");

    const mic = new ContinuousMic({ mode: "manual" });
    this.mic = mic;
    await mic.start({
      onLevel: (rms) => {
        if (this.orb.getState() === "listening") this.orb.setLevel(rms);
      },
      onAutoStop: (blob, filename) => {
        this.mic = null;
        if (!this.active) return;
        this.handleTranscription(blob, filename);
      },
      onError: (err) => {
        this.mic = null;
        this.fail(err);
      },
    });
  }

  /** Push-to-Talk: Selman lässt los → senden. */
  endPushToTalk(): void {
    if (!this.cfg.pushToTalk) return;
    if (!this.mic) return;
    // Mindest-Aufnahme-Zeit um Whisper-Fehler bei <100 ms zu vermeiden.
    setTimeout(() => {
      this.mic?.finish();
    }, 120);
  }

  /** PTT: ohne Senden abbrechen (z. B. Finger rausgezogen). */
  cancelPushToTalk(): void {
    if (!this.cfg.pushToTalk) return;
    if (this.mic) {
      this.mic.cancel();
      this.mic = null;
    }
    this.orb.setState("idle");
    this.orb.setLevel(0);
    if (this.active && !this.busy) {
      this.active = false;
      this.onActiveChange?.(false);
    }
  }

  /** Beendet die Session vollständig (Stop-Button). */
  stop(): void {
    this.active = false;
    this.cancelChat();
    this.stopAudio();
    if (this.mic) {
      this.mic.cancel();
      this.mic = null;
    }
    if (this.realtime) {
      // Realtime-Stop ist async aber wir warten nicht — Cleanup läuft im
      // Hintergrund, UI ist sofort idle.
      void this.realtime.stop();
      this.realtime = null;
      this.realtimeAssistantBubble = null;
      this.discardRealtimeUserBubble();
    }
    // Verwaiste Tool-Badges schließen (wir wissen nicht ob sie ok oder fail
    // waren — markieren als done, damit der Spinner weg ist).
    for (const badge of this.activeToolBadges.values()) badge.setStatus(true);
    this.activeToolBadges.clear();
    this.orb.setStatus(null);
    this.orb.setState("idle");
    this.muted = false;
    this.onActiveChange?.(false);
  }

  /**
   * Mikrofon temporär stumm — Session bleibt offen (kein Reconnect nötig).
   * Wirkt nur im Realtime-Mode (Classic-Mode kennt kein durchgehendes Mic).
   */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.realtime) {
      this.realtime.setMuted(muted);
    }
    this.orb.setStatus(muted ? "Stumm — Mikro aus" : null);
  }

  isMuted(): boolean {
    return this.muted;
  }

  private async startListening(): Promise<void> {
    this.stopAudio();
    if (this.mic) {
      this.mic.cancel();
      this.mic = null;
    }
    this.orb.setStatus(null);
    this.orb.setState("listening");

    const mic = new ContinuousMic();
    this.mic = mic;

    await mic.start({
      onLevel: (rms) => {
        if (this.orb.getState() === "listening") this.orb.setLevel(rms);
      },
      onSpeechStart: () => {
        // Optional: Status updaten — Selman sieht's am Orb-Wachstum.
      },
      onAutoStop: (blob, filename) => {
        this.mic = null;
        if (!this.active) return;
        this.handleTranscription(blob, filename);
      },
      onTimeout: () => {
        this.mic = null;
        if (!this.active) return;
        // Kein Sprechen erkannt → zurück ins Idle, aber Session bleibt aktiv.
        this.orb.setStatus("Nichts gehört — tippe noch mal");
        this.orb.setState("idle");
        // Nach 2s wieder neu hineinlauschen, falls Auto-Listen an.
        if (this.cfg.autoListen) {
          setTimeout(() => {
            if (this.active && this.orb.getState() === "idle" && !this.busy) {
              this.orb.setStatus(null);
              this.startListening().catch((err) => this.fail(err));
            }
          }, 1800);
        } else {
          this.active = false;
          this.onActiveChange?.(false);
        }
      },
      onError: (err) => {
        this.mic = null;
        this.fail(err);
      },
    });
  }

  private async handleTranscription(blob: Blob, filename: string): Promise<void> {
    this.busy = true;
    this.orb.setState("thinking");
    this.orb.setStatus("Verstehe Audio …");
    let transcript = "";
    try {
      transcript = await transcribeAudio(blob, filename);
    } catch (err) {
      this.busy = false;
      this.fail(err);
      return;
    }
    if (!transcript || !transcript.trim()) {
      this.busy = false;
      this.orb.setStatus("Konnte nichts verstehen — nochmal");
      this.orb.setState("idle");
      if (this.cfg.autoListen && !this.cfg.pushToTalk && this.active) {
        setTimeout(() => {
          if (this.active && !this.busy) {
            this.orb.setStatus(null);
            this.startListening().catch((err) => this.fail(err));
          }
        }, 1200);
      } else {
        this.active = false;
        this.onActiveChange?.(false);
      }
      return;
    }
    this.transcript.appendTurn("you", transcript);
    await this.runChatTurn(transcript);
  }

  private async runChatTurn(userText: string, opts?: { skipSpeak?: boolean }): Promise<void> {
    this.orb.setState("thinking");
    this.orb.setStatus(null);
    const stream = this.transcript.beginStreaming("jarvis");
    this.chatAbort = new AbortController();
    let aggregated = "";
    let final = "";
    let errored = false;

    try {
      await streamChat(userText, this.chatAbort.signal, {
        onThinking: () => {
          stream.bodyEl.classList.add("is-streaming");
        },
        onChunk: (piece) => {
          aggregated += piece;
          stream.append(piece);
        },
        onDone: (full) => {
          final = (full || aggregated).trim();
          stream.finalize(final);
          stream.bodyEl.classList.remove("is-streaming");
        },
        onError: (msg) => {
          errored = true;
          stream.finalize(`Fehler: ${msg}`);
          stream.bodyEl.classList.remove("is-streaming");
          stream.bubble.classList.add("bubble-error");
        },
      });
    } catch (err) {
      errored = true;
      stream.bodyEl.classList.remove("is-streaming");
      if (err instanceof DOMException && err.name === "AbortError") {
        // Nutzer hat abgebrochen — bewahre Partial-Text statt Error-Bubble.
        stream.finalize(aggregated);
      } else {
        const msg = err instanceof Error ? err.message : String(err);
        stream.finalize(`Fehler: ${msg}`);
        stream.bubble.classList.add("bubble-error");
      }
    } finally {
      this.chatAbort = null;
    }

    if (errored || !final) {
      this.busy = false;
      this.orb.setState("idle");
      this.orb.setStatus(null);
      if (this.cfg.autoListen && !this.cfg.pushToTalk && this.active) {
        await this.startListening();
      } else {
        this.active = false;
        this.onActiveChange?.(false);
      }
      return;
    }

    if (opts?.skipSpeak) {
      // Auto-/Text-Mode: keine TTS-Synthese. Bubble ist schon final geschrieben.
      this.busy = false;
      this.orb.setState("idle");
      this.orb.setStatus(null);
      this.active = false;
      this.onActiveChange?.(false);
      return;
    }
    await this.speakAndContinue(final);
  }

  private async speakAndContinue(text: string): Promise<void> {
    this.orb.setState("speaking");
    this.orb.setStatus(null);
    let url: string | null = null;
    try {
      const blob = await synthesize(text, this.cfg.voice);
      url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.crossOrigin = "anonymous";
      this.currentAudio = audio;
      this.audioAnalyser = attachAudioAnalyser(audio);

      // RMS → Orb-Level während Wiedergabe
      const tick = () => {
        if (!this.audioAnalyser || this.currentAudio !== audio) return;
        const rms = this.audioAnalyser.read();
        this.orb.setLevel(rms);
        this.speakingRafId = requestAnimationFrame(tick);
      };
      this.speakingRafId = requestAnimationFrame(tick);

      await new Promise<void>((resolve, reject) => {
        audio.addEventListener("ended", () => resolve());
        audio.addEventListener("error", () =>
          reject(new Error("Audio-Wiedergabe fehlgeschlagen")),
        );
        audio.play().catch((err) => reject(err));
      });
    } catch (err) {
      console.warn("TTS-Wiedergabe-Fehler:", err);
    } finally {
      this.stopAudio();
      if (url) URL.revokeObjectURL(url);
    }

    this.busy = false;
    if (!this.active) {
      this.orb.setState("idle");
      return;
    }
    if (this.cfg.autoListen && !this.cfg.pushToTalk) {
      await this.startListening();
    } else {
      // PTT oder Auto-Listen aus → zurück ins Idle, Session "wartet" bis Selman wieder drückt.
      this.active = false;
      this.orb.setState("idle");
      this.onActiveChange?.(false);
    }
  }

  private cancelChat(): void {
    if (this.chatAbort) {
      try {
        this.chatAbort.abort();
      } catch {
        // ignore
      }
      this.chatAbort = null;
    }
  }

  private stopAudio(): void {
    if (this.speakingRafId) cancelAnimationFrame(this.speakingRafId);
    this.speakingRafId = 0;
    if (this.audioAnalyser) {
      this.audioAnalyser.dispose();
      this.audioAnalyser = null;
    }
    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
      } catch {
        // ignore
      }
      this.currentAudio = null;
    }
  }

  private fail(err: unknown): void {
    this.busy = false;
    this.active = false;
    const e = err instanceof Error ? err : new Error(String(err));
    const msg = e.message || "Unbekannter Fehler";
    // Sichtbar machen: Orb-Status UND Transcript-Bubble — so sieht Selman es immer.
    this.transcript.appendTurn("error", msg);
    this.orb.setStatus(msg);
    this.orb.setState("error");
    this.onActiveChange?.(false);
    console.error("[jarvis] session fail:", e);
    setTimeout(() => {
      if (this.orb.getState() === "error") {
        this.orb.setStatus(null);
        this.orb.setState("idle");
      }
    }, 5000);
  }
}

/**
 * Versucht aus der Tool-Output-Summary eine Bild-URL zu extrahieren.
 * DALL-E-Tool gibt Lines wie "URL: https://oaidalleapi…" oder JSON-Strukturen
 * mit `image_url` / `url` zurück. Wir akzeptieren beides.
 */
function extractImageUrl(summary: string): string | null {
  if (!summary) return null;
  // JSON-Form
  try {
    const obj = JSON.parse(summary);
    if (obj && typeof obj === "object") {
      const candidates = [
        (obj as any).image_url,
        (obj as any).url,
        (obj as any).image,
        (obj as any).data?.[0]?.url,
      ];
      for (const c of candidates) {
        // http(s) ODER site-relative Pfade (z.B. /generated/x.png von gpt-image-1).
        // Relativ wird vom <img src> automatisch gegen den Origin aufgelöst.
        if (typeof c === "string" && (/^https?:\/\//.test(c) || /^\/[\w./-]+\.(?:png|jpe?g|webp|gif)$/i.test(c))) return c;
      }
    }
  } catch {
    // not JSON — fallback to line-scan
  }
  // Plain-text: "URL: https://…" oder einfach eine http(s)-URL irgendwo
  const urlMatch = summary.match(/https?:\/\/\S+\.(?:png|jpe?g|webp|gif)[^\s"']*/i)
    ?? summary.match(/https?:\/\/[^\s"']+/i);
  return urlMatch?.[0] ?? null;
}

function mergeTranscriptText(prev: string, next: string): string {
  const a = prev.trim();
  const b = next.trim();
  if (!a) return b;
  if (!b) return a;
  if (b.startsWith(a) || b.includes(a)) return b;
  if (a.startsWith(b) || a.includes(b)) return a;
  return `${a} ${b}`.trim();
}
